import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Locale } from "@anyseal/contracts";

const messages = {
  "zh-CN": {
    overview: "运营概览", channels: "供应商与通道", catalog: "模型目录", routing: "路由策略",
    users: "用户与权限", billing: "账务与支付", diagnostics: "请求诊断", audit: "审计与安全",
    operations: "任务与运行", risk: "风控与试用", growth: "内容与增长", deployments: "部署管理", settings: "系统设置",
    platform: "平台治理", operationsGroup: "运行控制", system: "系统", signOut: "退出", collapse: "收起导航", expand: "展开导航", language: "语言",
  },
  en: {
    overview: "Operations overview", channels: "Providers & channels", catalog: "Model catalog", routing: "Routing policies",
    users: "Users & access", billing: "Billing & payments", diagnostics: "Request diagnostics", audit: "Audit & security",
    operations: "Jobs & runtime", risk: "Risk & trials", growth: "Content & growth", deployments: "Deployments", settings: "System settings",
    platform: "Platform governance", operationsGroup: "Runtime control", system: "System", signOut: "Sign out", collapse: "Collapse navigation", expand: "Expand navigation", language: "Language",
  },
} as const;
type Key = keyof typeof messages["zh-CN"];
type Value = { locale: Locale; setLocale: (locale: Locale) => void; t: (key: Key) => string };
const Context = createContext<Value | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = localStorage.getItem("anyseal.admin.locale");
    if (stored === "zh-CN" || stored === "en") return stored;
    return navigator.language.toLowerCase().startsWith("zh") ? "zh-CN" : "en";
  });
  const setLocale = (value: Locale) => { localStorage.setItem("anyseal.admin.locale", value); setLocaleState(value); };
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);
  const value = useMemo<Value>(() => ({ locale, setLocale, t: (key) => messages[locale][key] }), [locale]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function useI18n(): Value { const value = useContext(Context); if (!value) throw new Error("Admin I18nProvider missing"); return value; }
